<template>
  <div class="container">
    <div class="row" v-if="hasData && showIndexDetail">
      <div class="col">
        <h5 class="card-header bg-primary text-white">Observation Finding Summary</h5>
        <div class="row m-3" v-if="testTypeHasObservationQuestions && !hasObservationQuestions">
          <CategoryIcon class="px-3 py-1" :categoryCode="'unknown'" />
          <span class="pt-2">The interviewee did not report any observations of counterproductive workplace behavior.</span>
        </div>
        <div v-else-if="testTypeHasObservationQuestions && hasObservationQuestions">
          <div class="row m-3">
            Note: This interview may contain findings about the interviewee's workplace observations that require follow up.
          </div>
          <div v-for="category in this.categories" :key="category.code" :class="getBreakStatus(category)" class="card mb-3">
            <div class="card-body p-0">
              <div class="container-fluid">
                <div class="row">
                  <div class="col">
                    <div class="row mt-2">
                      <div class="mt-2 ml-3">
                        <CategoryIcon :categoryCode="category.code" />
                      </div>
                      <div class="col">
                        <h4>{{ category.displayName }}</h4>
                      </div>
                    </div>
                    <div class="card my-2" v-for="comment in comments('category', category.code)" :key="comment.commentId">
                      <div class="card-body py-1">
                        <p class="font-italic">{{ comment.text }}</p>
                        <p class="mb-0 text-muted">{{ comment.userFullName }} on {{ formatter.date.long(comment.timestamp) }}</p>
                      </div>
                    </div>
                    <div class="row d-print-inline">
                      <div class="col px-0">
                        <table class="table mt-1" v-if="hasQuestionsToViewInCategory(category)">
                          <thead>
                            <tr>
                              <th></th>
                              <th class="text-left">Question</th>
                              <th class="text-center">Response</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="response in questionsToView(category)" :key="response.responseId">
                              <td class="align-top"><fa :icon="getResponseIcon(response)" size="lg" :class="getResponseIconClass(response)" /></td>
                              <td class="text-left align-top">
                                <span>{{ response.text }}</span>
                                <div class="alert alert-warning mt-2 mb-0 p-1" v-if="response.evasionCommentary">{{ response.evasionCommentary }}</div>
                                <div class="alert alert-secondary mt-2 mb-0 p-1" v-if="response.editorCode">{{ getResponseEditDescription(response) }}</div>
                                <div v-for="comment in comments('response', response.responseId)" :key="comment.commentId">
                                  <div class="card-body py-0 pt-1">
                                    <p class="font-italic">{{ comment.text }}</p>
                                    <p class="mb-0 text-muted">{{ comment.userFullName }} on {{ formatter.date.long(comment.timestamp) }}</p>
                                  </div>
                                </div>
                              </td>
                              <td class="text-center align-top text-capitalize">{{ getResponseDisplayName(response) }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import CategoryIcon from "@/views/reports/report/edit-report/CategoryIcon.vue";
import formatter from "@/utils/formatter";

export default {
  components: {
    CategoryIcon,
  },
  props: {
    organizationId: String,
    reportId: String,
  },
  data() {
    return {
      report: null,
    };
  },
  computed: {
    ...mapGetters("static", ["reportObservationEdits"]),
    formatter: () => formatter,
    hasData() {
      if (this.report && this.report.summary && this.report.summary.scoreCode === "inconclusive") return false;
      if (this.statusCode === "completed") return true;
      return false;
    },
    statusCode() {
      if (this.report && this.report.statusCode) return this.report.statusCode;
      return "unknown";
    },
    showIndexDetail() {
      return this.report != null && this.report.statusCode === "completed";
    },
    testTypeHasObservationQuestions() {
      if (this.report.registration.testCode == "periodic") return true;
      if (this.report.registration.testCode == "investigation") return true;
      return false;
    },
    hasObservationQuestions() {
      var categories = this.report ? (this.report.riskIndex ? this.report.riskIndex.categories : []) : [];
      for (var i = 0; i < categories.length; i++) {
        var category = categories[i];
        if (category == null || category.questions == null) continue;
        for (var j = 0; j < category.questions.length; j++) {
          var question = category.questions[j];
          if (question.groupCode === "5") {
            return true;
          }
        }
      }
      return false;
    },
    categories() {
      var categories = this.report ? (this.report.riskIndex ? this.report.riskIndex.categories : []) : [];
      var sortedCategories = categories.slice().filter(c => {
        // only show categories that contain Group 5 questions
        var questions = c.questions;
        for (var i = 0; i < questions.length; i++) {
          if (questions[i].groupCode == "5") return true;
        }
        return false;
      });

      sortedCategories.sort((x, y) => {
        if (this.sortProperty === "score") {
          var xMinScore = Math.min(x.originalScore, x.currentScore);
          var yMinScore = Math.min(y.originalScore, y.currentScore);
          if (xMinScore < yMinScore) return -1;
          if (xMinScore > yMinScore) return 1;
        }
        if (x.displayName < y.displayName) return -1;
        if (x.displayName > y.displayName) return 1;
        return 0;
      });

      sortedCategories.forEach(category => {
        category.questions.sort((x, y) => {
          var xSort = x.responseDisplayName !== x.preferredResponseDisplayName ? 1 : x.evasionCommentary ? 2 : 3;
          var ySort = y.responseDisplayName !== y.preferredResponseDisplayName ? 1 : y.evasionCommentary ? 2 : 3;
          if (xSort < ySort) return -1;
          if (xSort > ySort) return 1;
          return 0;
        });
      });

      return sortedCategories;
    },
  },
  created() {
    this.$axios
      .get(`/v1/organizations/${this.organizationId}/reports/${this.reportId}`)
      .then(res => (this.report = res.data))
      .catch(err => (this.error = err))
      .finally(() => this.print());
  },
  methods: {
    comments(subjectType, subjectId) {
      if (!this.report || !this.report.comments) return [];
      return this.report.comments.filter(c => c.subjectType === subjectType && c.subjectId == subjectId);
    },
    getBreakStatus(category) {
      if (this.shouldPageBreak(category)) {
        return "break-before";
      }
      return "";
    },
    shouldPageBreak(category) {
      const heightOfPage = 50;
      //find the previous categories
      var previousCategories = [];
      for (var i = 0; i < this.categories.length; i++) {
        var currentCategory = this.categories[i];
        if (currentCategory.code === category.code) {
          break;
        }
        previousCategories.push(currentCategory);
      }

      var totalHeight = 0;
      totalHeight += 5; // for first page header
      console.log("CALCULATING PREVIOUS CATEGORY HEIGHTS");
      for (var j = 0; j < previousCategories.length; j++) {
        var previousCategory = previousCategories[j];
        var height = this.getVerticalSpaceForCategory(previousCategory);
        console.log(`category: ${previousCategory.code} height: ${height}`);
        if (totalHeight + height >= heightOfPage) {
          console.log("going to next page, resetting height");
          totalHeight = height;
        } else {
          totalHeight += height;
        }
      }
      console.log(`total height on active page: ${totalHeight}`);

      var remainingSpace = heightOfPage - totalHeight;
      console.log(`remaining space on current page: ${remainingSpace}`);

      // calculation space for current cateogry
      var currentCategoryHeight = this.getVerticalSpaceForCategory(category);
      console.log(`current category: ${category.code} height: ${currentCategoryHeight}`);

      return currentCategoryHeight > remainingSpace;
    },
    getVerticalSpaceForCategory(category) {
      var totalHeight = 0;
      totalHeight += 5; // for title

      var categoryComments = this.comments("category", category.code);
      totalHeight += categoryComments.length * 4;

      totalHeight += 1.5; // question table header
      this.questionsToView(category).forEach(question => {
        totalHeight += question.text.length <= 85 ? 1.3 : 2;
        if (question.evasionCommentary) {
          totalHeight += question.evasionCommentary.length <= 100 ? 1.3 : 2;
        }
        if (question.editorCode) {
          totalHeight += this.getResponseEditDescription(question).length <= 100 ? 1.3 : 2;
        }

        var responseComments = this.comments("response", question.responseId);
        totalHeight += responseComments.length * 4;
      });

      totalHeight = Math.ceil(totalHeight);
      return totalHeight;
    },
    hasQuestionsToViewInCategory(category) {
      return this.questionsToView(category).length;
    },
    questionsToView(category) {
      return category.questions.filter(q => q.groupCode == "5");
    },
    getIcon(category) {
      if (!category.currentScore) return "circle";
      if (category.currentScore >= 70) return "check";
      if (category.currentScore >= 50) return "question";
      return "times";
    },
    getBgClass(category) {
      if (!category) return ["bg-secondary", "text-white"];
      if (this.isCategoryDisabled(category)) return ["bg-secondary", "text-white"];
      if (category.currentScore >= 70) return ["bg-success", "text-white"];
      if (category.currentScore >= 50) return ["bg-warning", "text-white"];
      return ["bg-danger", "text-white"];
    },
    getOriginalBgClass(category) {
      if (!category) return ["bg-secondary", "text-white"];
      if (this.isCategoryDisabled(category)) return ["bg-secondary", "text-white"];
      if (category.originalScore >= 70) return ["bg-success", "text-white"];
      if (category.originalScore >= 50) return ["bg-warning", "text-white"];
      return ["bg-danger", "text-white"];
    },
    getResponseIcon(response) {
      if (response.controlCode === "1") return "exclamation";
      if (response.evasionCommentary) return "exclamation";
      if (response.responseDisplayName !== response.preferredResponseDisplayName) return "exclamation";
      return "check";
    },
    getResponseIconClass(response) {
      if (response.controlCode === "1" && !response.evasionCommentary) return "text-danger";
      if (response.evasionCommentary) return "text-warning";
      if (response.responseDisplayName !== response.preferredResponseDisplayName) return "text-danger";
      return "text-success";
    },
    getResponseEditDescription(response) {
      var record = this.reportObservationEdits.find(c => c.code === response.editorCode);
      return record ? record.displayName : "---";
    },
    getResponseDisplayName(response) {
      return response.responseDisplayName.toLowerCase() == "timeout" ? "The examinee avoided answering this question" : response.responseDisplayName;
    },
    isCategoryDisabled(category) {
      return category.statusCode != "enabled";
    },
    showOriginalScore(category) {
      return category.originalScore != category.currentScore;
    },
    print() {
      setTimeout(() => (window.printReady = true), 500);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/base.scss";

table {
  @extend .mb-0;

  tr {
    cursor: pointer;
  }

  th,
  td {
    @extend .text-center;
    @extend .align-middle;

    h4,
    h5 {
      @extend .mb-0;
    }
  }
}

.break-before {
  page-break-before: always;

  @extend .mt-5;
}
</style>
